import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Plus, Calendar, MapPin, ImageIcon, ArrowRight, Eye, EyeOff, Trash2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/events").then((res) => setEvents(res.data));
  useEffect(() => { load(); }, []);

  const togglePublish = async (ev) => {
    try {
      const endpoint = ev.status === "published" ? "unpublish" : "publish";
      await api.post(`/admin/events/${ev.id}/${endpoint}`);
      toast.success(`Event ${endpoint}ed`);
      load();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}"? This removes all photos.`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="kicker mb-2">EVENTS</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter">Manage races</h1>
          <p className="mt-2 text-sm text-[#475569]">Create, publish, and manage every event on the platform.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-5 py-3 text-sm transition-colors"
          data-testid="new-event-btn"
        >
          <Plus size={14} /> New event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="border border-dashed border-[#E2E8F0] p-12 text-center bg-white" data-testid="events-empty-state">
          <p className="font-display text-xl text-[#0F172A] mb-2">No events yet</p>
          <p className="text-sm text-[#475569] mb-6">Create your first event to start uploading photos.</p>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-[#5B4CFF] text-white font-semibold px-5 py-2.5 text-sm">
            <Plus size={14} /> New event
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0]" data-testid="events-table">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-[#E2E8F0] text-[11px] font-mono tracking-widest text-[#475569]">
            <div className="col-span-4">EVENT</div>
            <div className="col-span-3">LOCATION & DATE</div>
            <div className="col-span-2">STATUS</div>
            <div className="col-span-1">PHOTOS</div>
            <div className="col-span-2 text-right">ACTIONS</div>
          </div>
          {events.map((ev) => (
            <div
              key={ev.id}
              className="grid grid-cols-1 md:grid-cols-12 px-6 py-4 border-b last:border-b-0 border-[#E2E8F0] items-center gap-4 hover:bg-[#F8FAFC] transition-colors"
              data-testid={`event-row-${ev.id}`}
            >
              <div className="md:col-span-4">
                <Link to={`/admin/events/${ev.id}`} className="font-display text-base font-semibold text-[#0F172A] hover:text-[#5B4CFF]">
                  {ev.name}
                </Link>
                <p className="text-xs text-[#475569] font-mono mt-1">/{ev.slug}</p>
              </div>
              <div className="md:col-span-3 text-sm text-[#0F172A]">
                <p className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-[#475569]" />{ev.city}, {ev.country}</p>
                <p className="inline-flex items-center gap-1.5 text-xs text-[#475569] mt-1"><Calendar size={12} />{ev.event_date}</p>
              </div>
              <div className="md:col-span-2">
                <span className={`inline-flex items-center px-2 py-1 text-[10px] font-semibold tracking-wider uppercase border ${
                  ev.status === "published" ? "bg-[#10B981] text-white border-[#10B981]" :
                  ev.status === "unpublished" ? "bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]" :
                  "bg-white text-[#0F172A] border-[#E2E8F0]"
                }`}>{ev.status}</span>
              </div>
              <div className="md:col-span-1 text-sm font-mono text-[#0F172A] inline-flex items-center gap-1">
                <ImageIcon size={12} className="text-[#475569]" /> {ev.photo_count}
              </div>
              <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                <button onClick={() => togglePublish(ev)} className="text-xs font-semibold inline-flex items-center gap-1 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-1.5 transition-colors" data-testid={`toggle-${ev.id}`}>
                  {ev.status === "published" ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                </button>
                <Link to={`/admin/events/${ev.id}/analytics`} className="text-xs font-semibold inline-flex items-center gap-1 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-1.5 transition-colors" data-testid={`analytics-${ev.id}`} title="Event analytics">
                  <BarChart3 size={12} /> Analytics
                </Link>
                <Link to={`/admin/events/${ev.id}`} className="text-xs font-semibold inline-flex items-center gap-1 bg-[#0F172A] text-white px-3 py-1.5">
                  Open <ArrowRight size={12} />
                </Link>
                <button onClick={() => remove(ev)} className="text-[#EF4444] hover:bg-[#fef2f2] p-1.5 border border-[#E2E8F0]" data-testid={`delete-${ev.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewEventModal
          onClose={() => setShowNew(false)}
          onCreated={(ev) => {
            setShowNew(false);
            toast.success("Event created");
            navigate(`/admin/events/${ev.id}`);
          }}
        />
      )}
    </div>
  );
}

function NewEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", banner_url: "", country: "", city: "",
    organizer_name: "", event_date: "", status: "draft",
  });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/events", form);
      onCreated(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="new-event-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E2E8F0] w-full max-w-2xl p-8"
      >
        <p className="kicker mb-2">NEW EVENT</p>
        <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-6">Create event</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Event name" required value={form.name} onChange={handle("name")} dt="ne-name" full />
          <Field label="Banner URL" value={form.banner_url} onChange={handle("banner_url")} dt="ne-banner" full placeholder="https://..." />
          <Field label="Country" required value={form.country} onChange={handle("country")} dt="ne-country" />
          <Field label="City" required value={form.city} onChange={handle("city")} dt="ne-city" />
          <Field label="Event date" required type="date" value={form.event_date} onChange={handle("event_date")} dt="ne-date" />
          <Field label="Organizer name" value={form.organizer_name} onChange={handle("organizer_name")} dt="ne-organizer" />
          <div className="md:col-span-2">
            <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">DESCRIPTION</label>
            <textarea
              value={form.description}
              onChange={handle("description")}
              rows={3}
              className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
              data-testid="ne-description"
            />
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F172A] hover:bg-[#F8FAFC] px-5 py-2.5">Cancel</button>
          <button type="submit" disabled={loading} className="bg-[#5B4CFF] hover:bg-[#4a3dde] disabled:opacity-60 text-white font-semibold px-5 py-2.5 text-sm" data-testid="ne-submit">
            {loading ? "Creating…" : "Create event"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, dt, full, ...props }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">{label.toUpperCase()}</label>
      <input
        {...props}
        className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
        data-testid={dt}
      />
    </div>
  );
}
